import React, { useCallback, useContext, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { ReactNode } from 'react';

import { NotificationService, NOTIFICATION_TYPE_SUCCESS, NOTIFICATION_DURATIONS } from 'ui-components/services/notification-service';
import { noop } from 'ui-components/default-props/noop';

import { useModal } from '../../../hooks/use-modal';
import { useResolveConnectedProviderId } from '../../../hooks/freight/use-resolve-connected-provider-id';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../tracking/tracking-constants';
import { FreightProviderRegistrationModal } from '.';

import { messages } from './messages';

type ConnectFreightProviderContextValue = {
  onConnectFreightProvider: (apiCode: string) => void;
  trackCarrierConnectionStep: (step: string, data?: Record<string, unknown>) => void;
  trackSegmentError: (error: unknown) => void;
};

export const ConnectFreightProviderContext = React.createContext<ConnectFreightProviderContextValue | undefined>(undefined);

export const useConnectFreightProviderContext = () => useContext(ConnectFreightProviderContext)!;

type ConnectFreightProviderModalContextProviderProps = {
  children: ReactNode;
  onCuiError?: (error: unknown) => void;
  onRegistrationBack?: () => void;
  onRegistrationFailure?: (error: unknown) => void;
  onRegistrationSuccess?: (sellerFreightProviderId: number | null) => void;
};

export const ConnectFreightProviderModalContextProvider = ({
  children,
  onCuiError = noop,
  onRegistrationBack = noop,
  onRegistrationFailure = noop,
  onRegistrationSuccess = noop,
}: ConnectFreightProviderModalContextProviderProps) => {
  const resolveConnectedProviderId = useResolveConnectedProviderId();
  const { close, isOpen, open } = useModal();
  const [providerApiCode, setProviderApiCode] = useState<string | undefined>(undefined);
  const trackCarrierConnectionStep = useTrackWorkflow(Workflows.CARRIER_CONNECTION_FLOW);

  const onConnectFreightProvider = useCallback(
    (apiCode: string) => {
      setProviderApiCode(apiCode);
      open();
    },
    [open],
  );

  const trackSegmentError = useCallback(
    (error: unknown) => {
      trackCarrierConnectionStep(WorkflowSteps.ERROR, { error, provider: providerApiCode, is_freight: true });
    },
    [trackCarrierConnectionStep, providerApiCode],
  );

  const onRegisterSuccess = useCallback(async () => {
    close();
    trackCarrierConnectionStep(WorkflowSteps.COMPLETE, { provider: providerApiCode, is_freight: true });

    // CUI's own success callback doesn't expose the new provider id — onRegistrationSuccess
    // always resolves with a real id instead of the caller having to wait for a separate
    // refresh (resolveConnectedProviderId falls back to null if the fetch itself fails, which
    // callers treat as "connected, but couldn't confirm which account" rather than a failure).
    const sellerFreightProviderId = await resolveConnectedProviderId();

    onRegistrationSuccess(sellerFreightProviderId);
    NotificationService.showNotification({
      title: <FormattedMessage {...messages.notificationTitle} />,
      notificationType: NOTIFICATION_TYPE_SUCCESS,
      content: <FormattedMessage {...messages.notificationBody} />,
      duration: NOTIFICATION_DURATIONS.MEDIUM,
    });
  }, [close, trackCarrierConnectionStep, onRegistrationSuccess, resolveConnectedProviderId, providerApiCode]);

  const onRegisterFailure = useCallback(
    (error: unknown) => {
      close();
      trackSegmentError(error);
      onRegistrationFailure(error);
    },
    [close, trackSegmentError, onRegistrationFailure],
  );

  const onError = useCallback(
    (error: unknown) => {
      trackSegmentError(error);
      onCuiError(error);
    },
    [trackSegmentError, onCuiError],
  );

  const onConnectCancel = useCallback(() => {
    trackCarrierConnectionStep(WorkflowSteps.CANCEL, { provider: providerApiCode, is_freight: true });
    close();
  }, [close, trackCarrierConnectionStep, providerApiCode]);

  // CUI's own "Back" link always unmounts its registration tree (see DirectCarrierRegistration's
  // onDone), regardless of what onBack does — so the host must close its modal here too, or the
  // modal chrome stays open around a now-empty body.
  const onConnectBack = useCallback(() => {
    close();
    onRegistrationBack();
  }, [close, onRegistrationBack]);

  const value = useMemo(
    () => ({
      onConnectFreightProvider,
      trackCarrierConnectionStep,
      trackSegmentError,
    }),
    [onConnectFreightProvider, trackCarrierConnectionStep, trackSegmentError],
  );

  return (
    <ConnectFreightProviderContext.Provider value={value}>
      {children}
      {isOpen && (
        <FreightProviderRegistrationModal
          onBack={onConnectBack}
          onClose={onConnectCancel}
          onError={onError}
          onRegisterFailure={onRegisterFailure}
          onRegisterSuccess={onRegisterSuccess}
          providerApiCode={providerApiCode}
        />
      )}
    </ConnectFreightProviderContext.Provider>
  );
};
